import React, { Fragment, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Col, Row, Card, Dropdown, Table, Button } from "react-bootstrap";
import { db } from "../../../firebase";
import {
  collectionGroup,
  getDocs,
  collection,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import emergency from "../../../images/emergency-icon.svg";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useAuth } from "../../../context/AuthContext";
import {
  filterRecordsByStaffCustomerAccess,
  isStaffCustomerScopedUser,
} from "../../../utils/staffCustomerAccess";

const ACTIVE_URGENT_BOOKING_STATUSES = [
  "Sleep Test Scheduled",
  "Transferred To Center",
  "Assigned",
  "Technician Scored",
  "Assigned to Doctor",
  "Doctor Scored",
  "Post-Consult",
  "Sale Follow Up",
  "Sale Follow Up [MC]",
  "Sale Follow Up [STM]",
  "Ready for CPAP Purchase",
];

const BOOKING_URGENT_STATUS_OPTIONS = [
  "ทั้งหมด",
  ...ACTIVE_URGENT_BOOKING_STATUSES,
];

const BookingUrgent = () => {
  const { currentUser } = useAuth();
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState("ทั้งหมด"); // Default to "ทั้งหมด"
  // const [selectedNode, setSelectedNode] = useState("ทั้งหมด"); // Default to "ทั้งหมด"
  // var nodeList = ["ทั้งหมด"]
  const [nodeBy, setNodeBy] = useState("ทั้งหมด");
  const [nodeList, setNodeList] = useState([]);
  const [nodeInfo, setNodeInfo] = useState([]);
  const [staffAdminProfile, setStaffAdminProfile] = useState(null);
  // const [nodeIdDocs,setNodeIdDocs] = useState([])

  useEffect(() => {
    const fetchNodes = async () => {
      const nodesDocs = await getDocs(collection(db, "nodes"));
      let nodeInfoList = nodesDocs.docs.map((nodeDoc) => ({
        id: nodeDoc.id,
        ...nodeDoc.data(),
      }));

      if (isStaffCustomerScopedUser(currentUser)) {
        const adminSnap = await getDoc(
          doc(db, "admins", currentUser.displayName),
        );
        const adminData = adminSnap.exists() ? adminSnap.data() : {};
        const nodeCodes = Array.isArray(adminData.node)
          ? adminData.node
          : adminData.node
          ? [adminData.node]
          : [];
        nodeInfoList = nodeInfoList.filter((node) =>
          nodeCodes.includes(node.nodecode),
        );
        setStaffAdminProfile(adminData);
      }

      const allNodes = nodeInfoList.map((node) => node.nodename);

      setNodeList(["ทั้งหมด", ...allNodes]);
      setNodeInfo(nodeInfoList);
      if (isStaffCustomerScopedUser(currentUser) && nodeInfoList.length === 0) {
        setFilteredBookings([]);
        setLoading(false);
      }
    };

    fetchNodes();
  }, [currentUser]);

  useEffect(() => {
    if (nodeInfo.length === 0) return; // Wait for nodeInfo
    if (
      isStaffCustomerScopedUser(currentUser) &&
      staffAdminProfile === null
    ) {
      return;
    }

    const fetchAndFilterBookings = async () => {
      setLoading(true);
      try {
        // const snapshot = await getDocs(collectionGroup(db, "bookings"));
        let bookingsQuery;
        if (currentUser.roles[0] === "sleep_technician") {
          bookingsQuery = query(
            collectionGroup(db, "bookings"),
            where("extendedProps.techrecord", "==", currentUser.displayName),
          );
        } else if (
          isStaffCustomerScopedUser(currentUser) &&
          nodeInfo.length <= 30
        ) {
          bookingsQuery = query(
            collectionGroup(db, "bookings"),
            where(
              "extendedProps.node",
              "in",
              nodeInfo.map((node) => node.nodename),
            ),
          );
        } else {
          bookingsQuery = collectionGroup(db, "bookings");
        }

        const snapshot = await getDocs(bookingsQuery);

        const todayDate = new Date();

        const allowedNodeNames = new Set(nodeInfo.map((node) => node.nodename));
        const visibleBookingData = filterRecordsByStaffCustomerAccess({
          records: snapshot.docs
            .map((bookingDoc) => ({
              ...bookingDoc.data(),
              _bookingDocumentId: bookingDoc.id,
            }))
            .filter(
              (booking) =>
                !isStaffCustomerScopedUser(currentUser) ||
                allowedNodeNames.has(
                  booking.extendedProps && booking.extendedProps.node,
                ),
            ),
          currentUser,
          adminData: staffAdminProfile,
          nodes: nodeInfo,
          getNodeValue: (booking) =>
            booking.extendedProps && booking.extendedProps.node,
          getCustomerValue: (booking) =>
            booking.extendedProps && booking.extendedProps.customer,
        });

        const filteredData = visibleBookingData
          .map((data) => {
            const { healthInsurance, latestSleepStudyDate, status, node } =
              data.extendedProps || {};
            const latestSleepStudyDateObj = new Date(latestSleepStudyDate);

            // Calculate deadline days
            let calculateDaysInitial =
              healthInsurance === "ชำระเงินเอง"
                ? 7
                : nodeInfo.find((n) => n.nodename === node).dueDays || 30;

            if (latestSleepStudyDateObj > todayDate) return null;

            const daysDiff =
              (todayDate - latestSleepStudyDateObj) / (1000 * 60 * 60 * 24);

            let alert = false;
            let hurry = false;

            if (healthInsurance === "ชำระเงินเอง") {
              if (daysDiff > calculateDaysInitial - 1 && daysDiff < 8) {
                alert = true;
              } else if (daysDiff > calculateDaysInitial + 1) {
                alert = true;
                hurry = true;
              }
            } else {
              if (
                daysDiff > 0.7 * calculateDaysInitial &&
                daysDiff < calculateDaysInitial
              ) {
                alert = true;
              } else if (daysDiff > calculateDaysInitial) {
                alert = true;
                hurry = true;
              }
            }

            data.extendedProps.deadline = calculateDaysInitial - daysDiff;

            return {
              id: data._bookingDocumentId,
              ...data,
              alert,
              hurry,
            };
          })
          .filter(Boolean)
          .filter((data) => {
            const { healthInsurance, latestSleepStudyDate, status, node } =
              data.extendedProps || {};
            const daysDiff =
              (todayDate - new Date(latestSleepStudyDate)) /
              (1000 * 60 * 60 * 24);
            let calculateDaysInitial =
              healthInsurance === "ชำระเงินเอง"
                ? 7
                : nodeInfo.find((n) => n.nodename === node).dueDays || 30;

            return (
              daysDiff > 0.4 * calculateDaysInitial &&
              ACTIVE_URGENT_BOOKING_STATUSES.includes(status)
            );
          })
          .filter((data) =>
            nodeBy === "ทั้งหมด" ? true : data.extendedProps.node === nodeBy,
          )
          .sort((a, b) => {
            // Sort by insurance type then by closest deadline
            if (
              a.extendedProps.healthInsurance === "ชำระเงินเอง" &&
              b.extendedProps.healthInsurance !== "ชำระเงินเอง"
            )
              return -1;
            if (
              a.extendedProps.healthInsurance !== "ชำระเงินเอง" &&
              b.extendedProps.healthInsurance === "ชำระเงินเอง"
            )
              return 1;

            return a.extendedProps.deadline - b.extendedProps.deadline;
          });

        const statusFilter =
          selectedStatus === "ทั้งหมด"
            ? ACTIVE_URGENT_BOOKING_STATUSES
            : [selectedStatus];

        const finalFilteredData = filteredData.filter((d) =>
          statusFilter.includes(d.extendedProps.status),
        );

        setFilteredBookings(finalFilteredData);
      } catch (error) {
        console.error("Error fetching and filtering bookings:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAndFilterBookings();
  }, [selectedStatus, nodeBy, nodeInfo, staffAdminProfile, currentUser]);

  // const handleExportToExcel = () => {
  //   console.log("Exporting to Excel:", filteredBookings);
  //   const data = filteredBookings.map((element, index) => ({
  //     No: index + 1,
  //     "Latest Sleep Study Date": element.extendedProps.latestSleepStudyDate,
  //     "Deadline in (days)": roundAwayFromZero(element.extendedProps.deadline),
  //     "Health Insurance": element.extendedProps.healthInsurance,
  //     Order: element.extendedProps.order,
  //     Name: `${element.extendedProps.firstname} ${element.extendedProps.lastname}`,
  //     HN: element.extendedProps.hn,
  //     Type: element.extendedProps.type,
  //     "Tech Record": element.extendedProps.techrecord,
  //     Doctor: element.extendedProps.doctor,
  //     Status: element.extendedProps.status,
  //     Alert:
  //       element.alert && !element.hurry
  //         ? "⏰ Warning"
  //         : element.alert && element.hurry
  //         ? "🚨 Emergency"
  //         : "Reminder",
  //   }));

  //   const worksheet = XLSX.utils.json_to_sheet(data);
  //   const workbook = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(workbook, worksheet, "Bookings");

  //   const excelBuffer = XLSX.write(workbook, {
  //     bookType: "xlsx",
  //     type: "array",
  //   });

  //   const dataBlob = new Blob([excelBuffer], {
  //     type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
  //   });

  //   saveAs(dataBlob, "Urgent Cases.xlsx");
  // };

  const handleExportToExcel = () => {
    console.log("Exporting to Excel:", filteredBookings);

    // Helper to map bookings -> row object
    const mapBookingsToRows = (bookings) =>
      bookings.map((element, index) => ({
        No: index + 1,
        "Latest Sleep Study Date": element.extendedProps.latestSleepStudyDate,
        "Deadline in (days)": roundAwayFromZero(element.extendedProps.deadline),
        "Health Insurance": element.extendedProps.healthInsurance,
        Order: element.extendedProps.order,
        Name: `${element.extendedProps.firstname} ${element.extendedProps.lastname}`,
        HN: element.extendedProps.hn,
        Type: element.extendedProps.type,
        "Tech Record": element.extendedProps.techrecord,
        Doctor: element.extendedProps.doctor,
        Status: element.extendedProps.status,
        Alert:
          element.alert && !element.hurry
            ? "⏰ Warning"
            : element.alert && element.hurry
              ? "🚨 Emergency"
              : "Reminder",
      }));

    // 1) Original order (summary)
    const summaryData = mapBookingsToRows(filteredBookings);

    // 2) Sorted by techrecord
    const techSortedBookings = [...filteredBookings].sort((a, b) => {
      const ta = a.extendedProps.techrecord || "";
      const tb = b.extendedProps.techrecord || "";
      return ta.localeCompare(tb);
    });
    const techData = mapBookingsToRows(techSortedBookings);

    // 3) Sorted by doctor
    const doctorSortedBookings = [...filteredBookings].sort((a, b) => {
      const da = a.extendedProps.doctor || "";
      const db = b.extendedProps.doctor || "";
      return da.localeCompare(db);
    });
    const doctorData = mapBookingsToRows(doctorSortedBookings);

    // Create workbook + 3 sheets
    const workbook = XLSX.utils.book_new();

    const wsSummary = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, wsSummary, "Summary");

    const wsTech = XLSX.utils.json_to_sheet(techData);
    XLSX.utils.book_append_sheet(workbook, wsTech, "By Tech");

    const wsDoctor = XLSX.utils.json_to_sheet(doctorData);
    XLSX.utils.book_append_sheet(workbook, wsDoctor, "By Doctor");

    // Write and download
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const dataBlob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    saveAs(dataBlob, "Urgent Cases.xlsx");
  };

  const roundAwayFromZero = (value) =>
    value < 0 ? Math.floor(value) : Math.ceil(value);

  //   if (loading) {
  //     return <div>Loading...</div>;
  //   }

  return (
    <div>
      <div style={{ padding: "10px 0" }}>
        <h4>
          Filter active booking stages through Ready for CPAP Purchase
        </h4>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginTop: "8px",
            paddingLeft: "20px",
          }}
        >
          {/* Empty space for icon alignment */}
          <div
            style={{ width: "2em", height: "1.6em", marginRight: "10px" }}
          ></div>
          <span>Reminder: ≥ 40% of due days</span>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginTop: "8px",
            paddingLeft: "20px",
          }}
        >
          <i
            className="flaticon-381-alarm-clock"
            style={{
              width: "1.4em",
              fontSize: "1.4em",
              marginRight: "10px",
              textAlign: "center",
            }}
          ></i>
          <span>Warning: ≥ 70% of due days</span>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginTop: "8px",
            paddingLeft: "20px",
          }}
        >
          <img
            src={emergency}
            alt="Emergency Icon"
            style={{
              width: "2em",
              height: "1.6em",
              marginRight: "10px",
              objectFit: "contain",
            }}
          />
          <span>Emergency: ≥ 100% of due days</span>
        </div>
      </div>

      <h3 className="mt-3">Deadline</h3>
      <h4>1 day = 1 day left until the deadline</h4>
      <h4>-1 day = 1 day past the deadline</h4>

      {/* Dropdown for Status Filter */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap", // optional for responsiveness
        }}
      >
        <div style={{ display: "flex", flexDirection: "row" }}>
          <Dropdown className="m-3">
            <Dropdown.Toggle variant="success" id="dropdown-basic">
              Select Status {selectedStatus}
            </Dropdown.Toggle>

            <Dropdown.Menu>
              {BOOKING_URGENT_STATUS_OPTIONS.map((status, index) => (
                <Dropdown.Item
                  key={index}
                  onClick={() => setSelectedStatus(status)}
                >
                  {status}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <Dropdown className="m-3">
            <Dropdown.Toggle variant="success" id="dropdown-basic">
              Select Node {nodeBy}
            </Dropdown.Toggle>

            <Dropdown.Menu>
              {nodeList.map((node, index) => (
                <Dropdown.Item key={index} onClick={() => setNodeBy(node)}>
                  {node}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <div className="mr-3">
          <Button onClick={handleExportToExcel}>Export to Excel</Button>
        </div>
      </div>
      {!loading && (
        <Row>
          <Col lg={12}>
            <Card>
              <Card.Body className="card-body">
                <div className="table-responsive">
                  <Table className="mb-0 table-striped">
                    <thead>
                      <tr>
                        <th className="py-0 text-center align-middle"> </th>
                        <th className="py-0 text-center align-middle"> </th>
                        <th className="py-0 text-center align-middle">
                          Sleep Study Date
                        </th>
                        <th className="py-0 text-center align-middle">
                          Deadline in (days)
                        </th>
                        <th className="py-0 text-center align-middle">
                          Health Insurance
                        </th>
                        <th className="py-0 text-center align-middle">
                          Due (days)
                        </th>
                        <th className="py-0 text-center align-middle">
                          Stop-Bang Score
                        </th>
                        <th className="py-0 text-center align-middle">
                          STATUS
                        </th>
                        <th className="py-0 text-center align-middle">
                          Sleep Study
                        </th>
                        <th className="py-0 text-center align-middle">Name</th>
                        {/* <th className="py-0 text-center align-middle">HN</th> */}
                        <th className="py-0 text-center align-middle">
                          Tech Record
                        </th>
                        <th className="py-0 text-center align-middle">
                          Tech Score
                        </th>
                        <th className="py-0 text-center align-middle">
                          Doctor
                        </th>
                        <th className="py-0 text-center align-middle">Note</th>
                      </tr>
                    </thead>
                    <tbody id="booking-table">
                      {filteredBookings
                        .sort((a, b) => {
                          // Step 1: Prioritize "ชำระเงินเอง"
                          const aCash =
                            a.extendedProps.healthInsurance === "ชำระเงินเอง";
                          const bCash =
                            b.extendedProps.healthInsurance === "ชำระเงินเอง";

                          if (aCash && !bCash) return -1;
                          if (!aCash && bCash) return 1;

                          // Step 2: Sort by deadline ascending
                          return (
                            a.extendedProps.deadline - b.extendedProps.deadline
                          );
                        })
                        .map((element, index) => {
                          var dueDays = 30;
                          if (
                            element.extendedProps.healthInsurance ===
                            "ชำระเงินเอง"
                          ) {
                            dueDays = 7;
                          } else {
                            const nodeInfoItem = nodeInfo.find(
                              (node) =>
                                node.nodename === element.extendedProps.node,
                            );
                            if (nodeInfoItem) {
                              dueDays = nodeInfoItem.dueDays || 30;
                            }
                          }

                          var typeSymbol = "";
                          var type = element.extendedProps.type;
                          if (type === "Split Night Sleep Study") {
                            typeSymbol = "[SN]";
                          } else if (type === "Full Night Sleep Study") {
                            typeSymbol = "[FN]";
                          } else if (type === "Home Full Night Sleep Study") {
                            typeSymbol = "[HN]";
                          } else if (
                            type === "CPAP Titration" ||
                            type === "CPAP Titration Study"
                          ) {
                            typeSymbol = "[CT]";
                          } else if (
                            type === "Overnight Pulse Oximetry Study"
                          ) {
                            typeSymbol = "[OPO]";
                          } else if (type === "Multiple Sleep Latency Test") {
                            typeSymbol = "[MSLT]";
                          }
                          return (
                            <tr key={element.id}>
                              <td className="py-0 text-center align-middle">
                                {index + 1}
                              </td>
                              <td className="py-0 text-center align-middle">
                                {element.alert && !element.hurry ? (
                                  <i className="flaticon-381-alarm-clock"></i>
                                ) : element.alert && element.hurry ? (
                                  <img
                                    src={emergency}
                                    alt="Emergency Icon"
                                    style={{ width: "2em", height: "2em" }}
                                  />
                                ) : null}
                              </td>
                              <td
                                className="py-0 text-center align-middle"
                                style={{ whiteSpace: "nowrap" }}
                              >
                                {element.extendedProps.latestSleepStudyDate}
                              </td>
                              <td className="py-0 text-center align-middle">
                                {roundAwayFromZero(
                                  element.extendedProps.deadline,
                                )}
                              </td>
                              <td className="py-0 text-center align-middle">
                                {element.extendedProps.healthInsurance}
                              </td>
                              <td className="py-0 text-center align-middle">
                                {dueDays}
                              </td>
                              <td className="py-0 text-center align-middle">
                                {element.extendedProps.stopBangScore}
                              </td>
                              <td className="py-0 text-center align-middle">
                                {element.extendedProps.status}
                              </td>
                              <td
                                className="py-0 text-center align-middle"
                                style={{ whiteSpace: "nowrap" }}
                              >
                                <Link to="#">
                                  {element.extendedProps.order} {typeSymbol}
                                </Link>
                              </td>
                              <td
                                className="py-0 text-center align-middle"
                                style={{ whiteSpace: "nowrap" }}
                              >
                                <Link
                                  to={`/patient-page/${element.extendedProps.hn}`}
                                >
                                  {element.extendedProps.firstname}{" "}
                                  {element.extendedProps.lastname}
                                </Link>
                              </td>
                              {/* <td
                              className="py-0 text-center align-middle"
                              style={{ whiteSpace: "nowrap" }}
                            >
                              <Link
                                to={`/patient-page/${element.extendedProps.hn}`}
                              >
                                {element.extendedProps.hn}
                              </Link>
                            </td> */}
                              <td className="py-0 text-center align-middle">
                                {element.extendedProps.techrecord}
                              </td>
                              <td className="py-0 text-center align-middle">
                                {element.extendedProps.technician}
                              </td>
                              <td className="py-0 text-center align-middle">
                                {element.extendedProps.doctor}
                              </td>
                              <td className="py-0 text-center align-middle">
                                {element.extendedProps.note}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default BookingUrgent;
